import React, { FC, useState } from "react"
import UniversalSignUp from "../../Common/UniversalSignup/UniversalSignup";
import Navbar from "../../Common/Navbar/Navbar";
import { LoginSuccessResponseStatus, SDKConfig } from "@ul/sdk";
import { UserPreference, LoginSucessAdditional } from "@ul/common"
import { DefaultUL } from "../../../constants/common/DefaultULSdk";
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { parseSignUpType } from '../../../utils/getSignUpType';
import { log } from "../../../utils/logUtils";
import { ULContainer } from "../../Common/Layout/ULContainer.styled";
import ThankyouSignupModal from "../../Common/SignupSuccess/SignupSuccess";
import signup from "../../Services/ajaxService";
import { PageType } from "../../../constants/common/pageType";
import {
    SignUpContainer,
    SignUpTitle,
    SignUpLayout,
    SwitchPartnerTypeHint,
} from '../../Common/UniversalSignup/UniversalSignup.styled';
import { CmsIds } from "../../../constants/common/CmsIds";
import { SignUpTypes } from "../../../constants/common/signUpType";
import { Link } from "@kite-js/core";
import { getServerSignupPartnerType } from "../../../utils/signupPartnerTypeCookie";

interface SignUpProps {
    locale?: string
    cmsItems: Cms.CmsDictionary
}

const getStatus = (status: number) => {
    switch (status) {
        case 200:
            return LoginSuccessResponseStatus.Success;
        case 403:
        case 401:
            return LoginSuccessResponseStatus.Fail;
        default:
            return LoginSuccessResponseStatus.Error;
    }
};

const SignUp: FC<SignUpProps> = ({ cmsItems, locale }) => {
    const isMobile = useIsMobile();
    const [searchParam] = useSearchParams()
    const params = useParams<{ culture?: string }>();
    const location = useLocation();
    const [signupSuccess, setSignupSuccess] = useState<boolean>(false);
    const [aid, setAid] = useState<number>(0);
    const signUpType = parseSignUpType(searchParam.get("psrc"))
    const hasPsrcInQuery = searchParam.has('psrc');
    const activePartnerType = getServerSignupPartnerType();
    const culturePath = params.culture ? `/${params.culture}` : '';
    const sdkConfig: SDKConfig = React.useMemo(() => ({
        ...DefaultUL,
        path: 'signup',
        cultureCode: locale ?? "en-us",
        iframe: {
            style: {
                'min-width': isMobile ? "315px" : "500px",
                'padding': "0",
                'background': "white",
                "border-radius": "20px"
            },
        },
        eventHandlers: {
            onLoginSuccess: async (_additional?: LoginSucessAdditional) => {
                log('onLoginSuccess');
                const typeForSignup = hasPsrcInQuery ? signUpType : activePartnerType!;
                const result = await signup(typeForSignup, _additional?.isSubscribeToPCEmail ?? false, PageType.SignUp);
                const status = getStatus(result.status);
                if (status === LoginSuccessResponseStatus.Success) {
                    setSignupSuccess(true);
                    if (result.affiliateId) setAid(result.affiliateId);
                }
                return {
                    loginStatus: status,
                };
            },
            onLoginComplete: (_success: boolean, _userPreference?: UserPreference) => {
                return 0;
            },
        },
        partnerCenterConfig: {
            signUpType,
        },
    }), [locale, signUpType, hasPsrcInQuery, activePartnerType])

    const getSignupFormTitle = () => {
        if (activePartnerType === SignUpTypes.Affiliate) {
            return cmsItems[CmsIds.SignupAsAffiliate];
        }
        if (activePartnerType === SignUpTypes.Ambassador) {
            return cmsItems[CmsIds.SignupAsAmbassador];
        }
        return cmsItems[CmsIds.SignupTitle];
    }

    const switchHint = (() => {
        if (activePartnerType !== SignUpTypes.Affiliate
            && activePartnerType !== SignUpTypes.Ambassador) {
            return null;
        }
        const cmsId =
            activePartnerType === SignUpTypes.Affiliate
                ? CmsIds.AmbassadorInstead
                : CmsIds.AffiliateInstead;
        const target = `${culturePath}/signuptype${location.search}`;
        const handleSwitch = (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            window.location.href = target;
        };
        return (
            <SwitchPartnerTypeHint data-testid="signup-switch-partner-type">
                {cmsItems[cmsId]}
                <Link
                    key="switch-partner-type"
                    href={target}
                    onClick={handleSwitch}
                >
                    {cmsItems[CmsIds.SwitchPartnerTypeClickHere]}
                </Link>
            </SwitchPartnerTypeHint>
        );
    })();

    return <ULContainer>
        <Navbar cmsItems={cmsItems} />
        <SignUpLayout>
            <SignUpContainer>
                <SignUpTitle>{getSignupFormTitle()}</SignUpTitle>
                {switchHint}
                <UniversalSignUp sdkConfig={sdkConfig} />
            </SignUpContainer>
        </SignUpLayout>
        <ThankyouSignupModal open={signupSuccess} cmsItems={cmsItems} signupType={signUpType} aid={aid}/>
    </ULContainer>
}

export default SignUp
